import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ScoreComparisonTable.css';

function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(+n)) return '—';
  const v = Number(n);
  return Number.isInteger(v) ? v.toString() : v.toFixed(1).replace('.', ',');
}

function Delta({ company, avg }) {
  if (company == null || avg == null) return null;
  const c = Number(company);
  const a = Number(avg);
  if (Number.isNaN(c) || Number.isNaN(a)) return null;
  if (c > a) return <span className="scorecard__delta scorecard__delta--up">+</span>;
  if (c < a) return <span className="scorecard__delta scorecard__delta--down">−</span>;
  return null;
}

function ScorecardHelpModal({ open, activeKey, onClose, onPrevAxis, onNextAxis }) {
  const { t } = useTranslation();

  const axisOrder = ['infrastructure', 'organization', 'processes', 'strategy'];

  const map = useMemo(
    () => ({
      infrastructure: {
        titleKey: 'result.content.chapter1.li1.title',
        contentKey: 'result.content.chapter1.li1.content',
        tableRowKey: 'result.content.chapter1.table.row1',
        fallbackTitle: 'Infrastructure',
      },
      organization: {
        titleKey: 'result.content.chapter1.li2.title',
        contentKey: 'result.content.chapter1.li2.content',
        tableRowKey: 'result.content.chapter1.table.row2',
        fallbackTitle: 'Organisation',
      },
      processes: {
        titleKey: 'result.content.chapter1.li3.title',
        contentKey: 'result.content.chapter1.li3.content',
        tableRowKey: 'result.content.chapter1.table.row3',
        fallbackTitle: 'Processus',
      },
      strategy: {
        titleKey: 'result.content.chapter1.li4.title',
        contentKey: 'result.content.chapter1.li4.content',
        tableRowKey: 'result.content.chapter1.table.row4',
        fallbackTitle: 'Stratégie',
      },
      global: {
        titleKey: 'result.title',
        contentKey: 'result.content.chapter2.p1',
        tableRowKey: null,
        fallbackTitle: 'Score global',
      },
    }),
    []
  );

  if (!open) return null;

  const cfg = map[activeKey] ?? map.infrastructure;

  const title = t(cfg.titleKey);
  const resolvedTitle = title && !title.includes(cfg.titleKey) ? title : cfg.fallbackTitle;

  const content = t(cfg.contentKey);
  const resolvedContent = content && !content.includes(cfg.contentKey) ? content : '…';

  const isAxis = axisOrder.includes(activeKey);
  const index = isAxis ? axisOrder.indexOf(activeKey) + 1 : null;
  const numberedTitle = isAxis ? `${index}. ${resolvedTitle}` : resolvedTitle;

  function getTopics(rowKey) {
    if (!rowKey) return [];
    const items = [];
    for (let i = 1; i <= 12; i += 1) {
      const k = `${rowKey}.c${i}`;
      const v = t(k);
      if (!v || v.includes(k)) break;
      items.push(v);
    }
    return items;
  }
  const topics = getTopics(cfg.tableRowKey);

  return (
    <div className="scmodal__backdrop" onClick={onClose}>
      <div
        className="scmodal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scmodal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="scmodal__header">
          <h3 id="scmodal-title">{t('result.content.chapter1.title')}</h3>
          <button
            className="scmodal__close"
            type="button"
            aria-label={t('header.close') || 'Fermer'}
            onClick={onClose}
          >
            x
          </button>
        </div>

        <div className="scmodal__single">
          <h3 className="scmodal__section-title">{numberedTitle}</h3>
          <p className="scmodal__placeholder">{resolvedContent}</p>

          {isAxis && topics.length > 0 && (
            <>
              <div className="scmodal__topics-title">
                {t('result.content.chapter1.table.head.th2')} :
              </div>
              <ul className="scmodal__list">
                {topics.map((item, idx) => (
                  <li key={idx} className="scmodal__list-item">
                    <img src="/icon-arrow-right.svg" alt="" aria-hidden="true" className="scmodal__list-icon" />
                    <span className="scmodal__list-text">{item}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {isAxis && (
          <div className="scmodal__footer">
            <button type="button" className="scmodal__nav scmodal__nav--prev" onClick={onPrevAxis}>
              {t('survey.pagination.previous') || 'Précédent'}
            </button>
            <button type="button" className="scmodal__nav scmodal__nav--next" onClick={onNextAxis}>
              {t('survey.pagination.next') || 'Suivant'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ScoreComparisonTable({
  t,
  companyName,
  companyDate,
  company = {},
  average = {},
  comparison = null,
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState('infrastructure');

  const axisOrder = ['infrastructure', 'organization', 'processes', 'strategy'];

  function detectComparisonInPath() {
    try {
      const href = window.location.href;
      const tail = decodeURIComponent(href.split('/survey/result/')[1] || '')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, '');
      return tail.includes('CUR=') && tail.includes('COMP=');
    } catch {
      return false;
    }
  }
  const hasComparisonParams = detectComparisonInPath();
  const showComparison = hasComparisonParams && !!comparison;

  const avgGlobal =
    average.global ??
    [average.infrastructure, average.organization, average.processes, average.strategy]
      .filter((v) => typeof v === 'number' && !Number.isNaN(v))
      .reduce((a, b, i, arr) => a + b / arr.length, 0);

  const rows = [
    { key: 'infrastructure', label: t('result.axis.infrastructure') },
    { key: 'organization', label: t('result.axis.organization') },
    { key: 'processes', label: t('result.axis.processes') },
    { key: 'strategy', label: t('result.axis.strategy') },
  ];

  function openHelp(sectionKey) {
    setHelpSection(sectionKey);
    setHelpOpen(true);
  }

  function goPrevAxis() {
    if (!axisOrder.includes(helpSection)) return;
    const i = axisOrder.indexOf(helpSection);
    const prev = i === 0 ? axisOrder[axisOrder.length - 1] : axisOrder[i - 1];
    setHelpSection(prev);
  }
  function goNextAxis() {
    if (!axisOrder.includes(helpSection)) return;
    const i = axisOrder.indexOf(helpSection);
    const next = i === axisOrder.length - 1 ? axisOrder[0] : axisOrder[i + 1];
    setHelpSection(next);
  }

  return (
    <div className="scorecard">
      <table className="scorecard__table" role="table">
        <thead>
          <tr>
            <th className="scorecard__th scorecard__th--labels" />
            <th className="scorecard__th scorecard__th--company">
              <div className="scorecard__th-title">
                <span>{t('result.table.col2')}</span>
              </div>
            </th>
            {showComparison && (
              <th className="scorecard__th scorecard__th--comparison">
                <div className="scorecard__th-title">
                  <span>{comparison.name || t('result.table.comparison')}</span>
                </div>
              </th>
            )}
            <th className="scorecard__th scorecard__th--avg">
              <div className="scorecard__th-title">
                <span>{t('result.histogram.mean') || 'Moyenne des entreprises'}</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const companyValue = Number(company[r.key]);
            const comparisonValue =
              comparison && comparison[r.key] != null ? Number(comparison[r.key]) : null;
            const averageValue = Number(average[r.key]);

            return (
              <tr key={r.key}>
                <th className="scorecard__rowlabel">
                  {r.label}
                  <button
                    className="scorecard__help"
                    type="button"
                    onClick={() => openHelp(r.key)}
                    aria-label={`Aide ${r.label}`}
                    title={`Aide ${r.label}`}
                  >
                    ?
                  </button>
                </th>

                <td className="scorecard__cell scorecard__cell--company">
                  <span className="scorecard__value">{fmt(companyValue)}</span>
                  <Delta company={companyValue} avg={averageValue} />
                </td>

                {showComparison && (
                  <td className="scorecard__cell scorecard__cell--comparison">
                    <span className="scorecard__value">{fmt(comparisonValue)}</span>
                  </td>
                )}

                <td className="scorecard__cell scorecard__cell--avg">
                  <span className="scorecard__value">{fmt(averageValue)}</span>
                </td>
              </tr>
            );
          })}

          <tr className="scorecard__global">
            <th className="scorecard__rowlabel scorecard__rowlabel--global">
              {t('result.title')}
              <button
                className="scorecard__help"
                type="button"
                onClick={() => openHelp('global')}
                aria-label="Aide score global"
                title="Aide score global"
              >
                ?
              </button>
            </th>

            <td className="scorecard__cell scorecard__cell--company">
              <span className="scorecard__value">
                {fmt(
                  company.global ?? company.globalScore ?? company.scoreGlobal ?? company.score
                )}
              </span>
              <Delta
                company={company.global ?? company.globalScore ?? company.scoreGlobal ?? company.score}
                avg={avgGlobal}
              />
            </td>

            {showComparison && (
              <td className="scorecard__cell scorecard__cell--comparison">
                <span className="scorecard__value">
                  {comparison && comparison.global != null ? fmt(comparison.global) : '—'}
                </span>
              </td>
            )}

            <td className="scorecard__cell scorecard__cell--avg">
              <span className="scorecard__value scorecard__value--green">{fmt(avgGlobal)}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <ScorecardHelpModal
        open={helpOpen}
        activeKey={helpSection}
        onClose={() => setHelpOpen(false)}
        onPrevAxis={goPrevAxis}
        onNextAxis={goNextAxis}
      />
    </div>
  );
}